<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

const { t, locale } = useI18n()
const isDark = ref(false)
const isMobileMenuOpen = ref(false)

const languages = [
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
  { code: 'id', label: 'Bahasa Indonesia', dir: 'ltr' }
]

const currentLang = computed(() => {
    return languages.find(l => l.code === locale.value) || languages[0]
})

const changeLanguage = (code: string) => {
    const lang = languages.find(l => l.code === code)
    if (lang) {
        locale.value = code
        localStorage.setItem('locale', code)
        document.documentElement.dir = lang.dir
        document.documentElement.lang = code
    }
}

const toggleTheme = () => {
  isDark.value = !isDark.value
  if (isDark.value) {
    document.documentElement.classList.add('dark')
    localStorage.setItem('theme', 'dark')
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.setItem('theme', 'light')
  }
}

const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value
    document.body.style.overflow = isMobileMenuOpen.value ? 'hidden' : ''
}

onMounted(() => {
  const savedTheme = localStorage.getItem('theme')
  if (savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    isDark.value = true
    document.documentElement.classList.add('dark')
  }
  const savedLang = localStorage.getItem('locale')
  if (savedLang) {
      const langConfig = languages.find(l => l.code === savedLang)
      if (langConfig) {
          document.documentElement.dir = langConfig.dir
          document.documentElement.lang = savedLang
      }
  }
})
</script>

<template>
  <div class="min-h-screen bg-[#FDFBF7] dark:bg-bg-primary text-bg-primary dark:text-text-primary font-sans transition-colors duration-300">
    <!-- Navigation -->
    <nav class="sticky top-0 z-50 bg-[#FDFBF7]/90 dark:bg-bg-primary/90 backdrop-blur-md border-b border-gray-200/50 dark:border-border transition-colors duration-300">
      <div class="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <RouterLink to="/" class="flex items-center">
          <img v-if="isDark" src="/images/logo_net_white.png" alt="Nomadian Logo" class="h-32 w-auto" />
          <img v-else src="/images/logo_net_black.png" alt="Nomadian Logo" class="h-32 w-auto" />
        </RouterLink>
        
        <!-- Desktop Nav -->
        <div class="hidden md:flex items-center gap-6">
            <div class="flex items-center space-x-8 rtl:space-x-reverse text-sm font-medium text-gray-800 dark:text-gray-200">
                <RouterLink to="/" class="hover:text-blue-600 dark:hover:text-white transition-colors">{{ t('footer.links.home') }}</RouterLink>
                <RouterLink to="/philosophy" class="hover:text-blue-600 dark:hover:text-white transition-colors">{{ t('nav.philosophy') }}</RouterLink>
                <RouterLink to="/about" class="hover:text-blue-600 dark:hover:text-white transition-colors">{{ t('nav.about') }}</RouterLink>
            </div>
            
            <div class="flex items-center gap-3 border-l pl-6 rtl:pl-0 rtl:border-l-0 rtl:border-r rtl:pr-6 border-gray-300 dark:border-gray-700">
                <div class="relative group">
                    <button class="flex items-center gap-1 text-sm font-medium hover:text-blue-600 dark:hover:text-white">
                        <span>{{ currentLang.code.toUpperCase() }}</span>
                        <span class="text-[10px]">▼</span>
                    </button>
                    <div class="absolute top-full right-0 rtl:right-auto rtl:left-0 mt-2 w-32 bg-white dark:bg-bg-card rounded-lg shadow-lg border border-gray-100 dark:border-border py-1 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all transform origin-top-right rtl:origin-top-left z-50">
                        <button 
                            v-for="lang in languages" 
                            :key="lang.code"
                            @click="changeLanguage(lang.code)"
                            class="w-full text-left rtl:text-right px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-bg-elevated transition-colors"
                            :class="{'font-bold text-blue-600': locale === lang.code}"
                        >
                            {{ lang.label }}
                        </button>
                    </div>
                </div>
                <button @click="toggleTheme" class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                    <span v-if="isDark">☀️</span>
                    <span v-else>🌙</span>
                </button>
            </div>
        </div>

        <!-- Mobile Toggle -->
        <div class="flex items-center gap-4 md:hidden">
            <button @click="toggleTheme" class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                <span v-if="isDark">☀️</span>
                <span v-else>🌙</span>
            </button>
            <button @click="toggleMobileMenu" class="p-2 -mr-2">
                <div class="w-6 h-5 flex flex-col justify-between">
                    <span class="w-full h-0.5 bg-current transform transition-transform" :class="{'rotate-45 translate-y-2': isMobileMenuOpen}"></span>
                    <span class="w-full h-0.5 bg-current opacity-100 transition-opacity" :class="{'opacity-0': isMobileMenuOpen}"></span>
                    <span class="w-full h-0.5 bg-current transform transition-transform" :class="{'-rotate-45 -translate-y-2.5': isMobileMenuOpen}"></span>
                </div>
            </button>
        </div>
      </div>
    </nav>

    <!-- Mobile Menu Overlay -->
    <div v-if="isMobileMenuOpen" class="fixed inset-0 z-40 bg-[#FDFBF7] dark:bg-bg-primary pt-24 px-6 md:hidden">
        <div class="flex flex-col space-y-6 text-xl font-medium">
             <RouterLink to="/" @click="toggleMobileMenu" class="border-b border-gray-200 dark:border-gray-800 pb-4">{{ t('footer.links.home') }}</RouterLink>
             <RouterLink to="/philosophy" @click="toggleMobileMenu" class="border-b border-gray-200 dark:border-gray-800 pb-4">{{ t('nav.philosophy') }}</RouterLink>
             <RouterLink to="/about" @click="toggleMobileMenu" class="border-b border-gray-200 dark:border-gray-800 pb-4">{{ t('nav.about') }}</RouterLink>
             
             <div class="pt-4 flex gap-2 overflow-x-auto pb-2">
                 <button 
                    v-for="lang in languages"
                    :key="lang.code"
                    @click="changeLanguage(lang.code)"
                    class="px-4 py-2 rounded-full border text-sm whitespace-nowrap"
                    :class="locale === lang.code ? 'border-black dark:border-white bg-black dark:bg-white text-white dark:text-black' : 'border-gray-300 dark:border-gray-700'"
                 >
                    {{ lang.label }}
                 </button>
             </div>
        </div>
    </div>

    <main class="relative overflow-hidden">
      <!-- Gradient Orb Background -->
      <div class="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-gradient-to-br from-orange-200 via-rose-200 to-blue-200 dark:from-orange-900/30 dark:via-purple-900/30 dark:to-blue-900/30 rounded-full blur-3xl opacity-50 pointer-events-none"></div>

      <!-- Geometric Accent Top Left -->
      <div class="absolute top-10 -left-20 w-80 h-80 text-[#B89B5E]/20 dark:text-[#D4AF37]/10 pointer-events-none -rotate-12 animate-[spin_120s_linear_infinite]">
          <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" stroke-width="0.25">
             <rect x="15" y="15" width="70" height="70" transform="rotate(45 50 50)" />
             <rect x="15" y="15" width="70" height="70" transform="rotate(0 50 50)" />
             <circle cx="50" cy="50" r="30" />
             <circle cx="50" cy="50" r="10" />
          </svg>
      </div>
      
      <!-- Geometric Accent Top Right -->
      <div class="absolute top-64 -right-32 w-96 h-96 text-[#8B7344]/20 dark:text-[#C5A059]/10 pointer-events-none rotate-45 animate-[spin_150s_linear_infinite_reverse]">
          <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" stroke-width="0.25">
             <rect x="10" y="10" width="80" height="80" transform="rotate(0 50 50)" />
             <rect x="10" y="10" width="80" height="80" transform="rotate(30 50 50)" />
             <rect x="10" y="10" width="80" height="80" transform="rotate(60 50 50)" />
             <circle cx="50" cy="50" r="28" />
          </svg>
      </div>

      <div class="relative z-10 max-w-4xl mx-auto px-6 pt-24 pb-32">
        <!-- Page Title -->
        <h1 class="text-6xl md:text-8xl font-serif font-medium text-center mb-6 text-bg-primary dark:text-white leading-tight">
            {{ t('pages.philosophy.title') }}
        </h1>
        
        <!-- Decorative Title Accents -->
        <div class="flex justify-center items-center mb-10 gap-4 text-[#D4AF37] dark:text-[#C5A059]">
            <div class="h-[1px] bg-current w-16 md:w-32 opacity-50"></div>
            <svg viewBox="0 0 24 24" class="w-5 h-5" fill="currentColor">
                <path d="M12 2 L14.5 9.5 L22 12 L14.5 14.5 L12 22 L9.5 14.5 L2 12 L9.5 9.5 Z" />
            </svg>
            <div class="h-[1px] bg-current w-16 md:w-32 opacity-50"></div>
        </div>

        <!-- Intro -->
        <p class="text-center text-lg md:text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto mb-24 leading-relaxed">
            {{ t('pages.philosophy.intro') }}
        </p>

        <!-- Section 1: Faith -->
        <article class="mb-20 pb-20 relative border-b border-gray-100 dark:border-gray-800">
            <div class="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                <div class="md:col-span-8 order-2 md:order-1">
                    <h2 class="text-3xl md:text-4xl font-serif mb-6 text-bg-primary dark:text-white leading-snug">
                        {{ t('pages.philosophy.faith.title') }}
                    </h2>
                    <p class="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                        {{ t('pages.philosophy.faith.desc') }}
                    </p>
                </div>
                <!-- Arabic geometric SVG (Rub el Hizb element) -->
                <div class="md:col-span-4 order-1 md:order-2 flex justify-start md:justify-end">
                    <svg viewBox="0 0 100 100" class="w-32 h-32 md:w-40 md:h-40 text-[#D4AF37] dark:text-[#C5A059] opacity-80 drop-shadow-sm" fill="none" stroke="currentColor" stroke-width="1">
                        <path d="M50 5 L65 35 L95 50 L65 65 L50 95 L35 65 L5 50 L35 35 Z" fill="currentColor" fill-opacity="0.05"/>
                        <rect x="25" y="25" width="50" height="50" transform="rotate(0 50 50)" />
                        <rect x="25" y="25" width="50" height="50" transform="rotate(45 50 50)" />
                        <circle cx="50" cy="50" r="16" stroke-dasharray="2 4" stroke-width="0.5" />
                        <circle cx="50" cy="50" r="4" fill="currentColor" />
                        <circle cx="50" cy="50" r="38" stroke-width="0.5" stroke-opacity="0.5" />
                    </svg>
                </div>
            </div>
            <!-- Decorative Separator Overlay -->
            <div class="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 text-[#D4AF37] dark:text-[#C5A059] bg-[#FDFBF7] dark:bg-bg-primary px-4 opacity-70">
                <svg viewBox="0 0 24 24" class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="1">
                   <rect x="7" y="7" width="10" height="10" transform="rotate(45 12 12)" />
                   <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                </svg>
            </div>
        </article>

        <!-- Section 2: Asset -->
        <article class="mb-20 pb-20 relative border-b border-gray-100 dark:border-gray-800">
            <div class="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                <!-- Interlocking Octagons SVG -->
                <div class="md:col-span-4 flex justify-start md:justify-start">
                    <svg viewBox="0 0 100 100" class="w-32 h-32 md:w-40 md:h-40 text-[#B89B5E] dark:text-[#8B7344] opacity-80 drop-shadow-sm" fill="none" stroke="currentColor" stroke-width="1">
                        <polygon points="30,10 70,10 90,30 90,70 70,90 30,90 10,70 10,30" stroke-opacity="0.7"/>
                        <polygon points="30,10 70,10 90,30 90,70 70,90 30,90 10,70 10,30" transform="rotate(22.5 50 50)" stroke-opacity="0.7"/>
                        <circle cx="50" cy="50" r="24" stroke-dasharray="3 5" stroke-width="0.5" />
                        <path d="M50 38 L62 50 L50 62 L38 50 Z" fill="currentColor" fill-opacity="0.05"/>
                        <circle cx="50" cy="50" r="42" stroke-width="0.25" stroke-opacity="0.8" />
                    </svg>
                </div>
                <div class="md:col-span-8">
                    <h2 class="text-3xl md:text-4xl font-serif mb-6 text-bg-primary dark:text-white leading-snug">
                        {{ t('pages.philosophy.asset.title') }}
                    </h2>
                    <p class="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                        {{ t('pages.philosophy.asset.desc') }}
                    </p>
                </div>
            </div>
            <!-- Decorative Separator Overlay -->
            <div class="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 text-[#D4AF37] dark:text-[#C5A059] bg-[#FDFBF7] dark:bg-bg-primary px-4 opacity-70">
                <svg viewBox="0 0 24 24" class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="1">
                   <rect x="7" y="7" width="10" height="10" transform="rotate(45 12 12)" />
                   <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                </svg>
            </div>
        </article>

        <!-- Section 3: Tech -->
        <article class="mb-10 relative">
            <div class="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                <div class="md:col-span-8 order-2 md:order-1">
                    <h2 class="text-3xl md:text-4xl font-serif mb-6 text-bg-primary dark:text-white leading-snug">
                        {{ t('pages.philosophy.accessible.title') }}
                    </h2>
                    <p class="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                        {{ t('pages.philosophy.accessible.desc') }}
                    </p>
                </div>
                <!-- Nested Hexagons SVG -->
                <div class="md:col-span-4 order-1 md:order-2 flex justify-start md:justify-end">
                    <svg viewBox="0 0 100 100" class="w-32 h-32 md:w-40 md:h-40 text-[#D4AF37] dark:text-[#C5A059] opacity-80 drop-shadow-sm" fill="none" stroke="currentColor" stroke-width="1">
                        <polygon points="50,10 84.64,30 84.64,70 50,90 15.36,70 15.36,30" fill="currentColor" fill-opacity="0.02" stroke-opacity="0.5" />
                        <polygon points="50,18 77.71,34 77.71,66 50,82 22.29,66 22.29,34" transform="rotate(30 50 50)" stroke-opacity="0.7"/>
                        <polygon points="50,28 69.05,39 69.05,61 50,72 30.95,61 30.95,39" stroke-width="0.75" />
                        <circle cx="50" cy="50" r="12" stroke-dasharray="2 4" stroke-width="0.5" />
                        <circle cx="50" cy="50" r="3" fill="currentColor" />
                    </svg>
                </div>
            </div>
            
            <!-- Bottom Line Accent -->
            <div class="flex justify-center mt-20 text-[#D4AF37] dark:text-[#C5A059] opacity-40">
                <svg viewBox="0 0 200 10" class="w-full max-w-lg h-4" preserveAspectRatio="none" fill="none" stroke="currentColor" stroke-width="0.5">
                    <path d="M0 5 h85 M85 5 l 5 -5 l 10 10 l 10 -10 l 10 10 l 5 -5 h85" />
                </svg>
            </div>
        </article>
      </div>
    </main>

    <!-- Footer -->
    <footer class="bg-bg-primary text-white py-20 px-6 dark:bg-black dark:border-t dark:border-border">
      <div class="max-w-7xl mx-auto">
        <div class="flex flex-col md:flex-row justify-between items-start mb-16 gap-12">
             <div class="font-bold text-2xl">Nomadian Elite</div>
             <div class="grid grid-cols-2 gap-16">
                 <div>
                     <h4 class="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">{{ t('footer.navTitle') }}</h4>
                     <div class="flex flex-col gap-3 text-sm text-gray-400">
                         <RouterLink to="/" class="hover:text-white transition-colors">{{ t('footer.links.home') }}</RouterLink>
                         <RouterLink to="/philosophy" class="hover:text-white transition-colors">{{ t('nav.philosophy') }}</RouterLink>
                         <RouterLink to="/about" class="hover:text-white transition-colors">{{ t('footer.links.about') }}</RouterLink>
                     </div>
                 </div>
                 <div>
                     <h4 class="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">{{ t('footer.legalTitle') }}</h4>
                     <div class="flex flex-col gap-3 text-sm text-gray-400">
                         <a href="#" class="hover:text-white transition-colors">{{ t('footer.links.privacy') }}</a>
                         <a href="#" class="hover:text-white transition-colors">{{ t('footer.links.terms') }}</a>
                     </div>
                 </div>
             </div>
        </div>
        
        <div class="border-t border-gray-800 pt-12 text-xs text-gray-500 leading-relaxed text-justify">
            <p class="mb-4">
               {{ t('footer.disclaimer1') }}
            </p>
            <p>
               {{ t('footer.disclaimer2') }}
            </p>
             <div class="mt-8 flex justify-between items-center">
                <span>{{ t('footer.copyright') }}</span>
                <div class="flex gap-4">
                    <span>Twitter</span>
                    <span>LinkedIn</span>
                </div>
             </div>
        </div>
      </div>
    </footer>
  </div>
</template>
